import { ArrowLeft, ArrowRight, Calendar, Share2, BookOpen, X, ZoomIn } from 'lucide-react';
import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  red: { bg: 'bg-red-100', text: 'text-red-700' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
  green: { bg: 'bg-green-100', text: 'text-green-700' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-700' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

export default function BlogPostPage({ postId }) {
  const slug = postId;
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchPost();
      fetchSettings();
      fetchRelatedPosts();
    }
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/blog/posts/${slug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Blog yazısı bulunamadı');
        } else {
          setError('Bir hata oluştu');
        }
        return;
      }
      
      const data = await response.json();
      setPost(data);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Blog yazısı yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts?limit=3`);
      const data = await response.json();
      // Filter out current post
      setRelatedPosts((data.posts || []).filter(p => p.slug !== slug).slice(0, 3));
    } catch (err) {
      console.error('Error fetching related posts:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/settings`);
      const data = await response.json();
      setSettings(data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link kopyalandı!');
    }
  };

  const getColorConfig = (colorValue) => {
    return categoryColors[colorValue] || categoryColors.gray;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-gradient-to-r from-red-950 via-red-900 to-orange-900">
          <div className="container mx-auto px-4 py-6">
            <button
              onClick={() => window.location.href = '/blog'}
              className="flex items-center gap-2 text-white hover:text-orange-300 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Bloga Dön
            </button>
          </div>
        </div>
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="text-6xl mb-4">📄</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{error || 'Blog Yazısı Bulunamadı'}</h1>
          <p className="text-gray-600 mb-8">Aradığınız yazı mevcut değil veya kaldırılmış olabilir.</p>
          <a
            href="/blog"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-red-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Tüm Yazılara Dön
          </a>
        </div>
      </div>
    );
  }

  const colorConfig = getColorConfig(post.category_color);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className={`bg-gradient-to-r ${post.gradient || 'from-red-600 to-orange-500'}`}>
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.location.href = '/blog'}
              className="flex items-center gap-2 text-white hover:text-white/80 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Bloga Dön
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-white hover:text-white/80 transition-all"
            >
              <Share2 className="w-5 h-5" />
              Paylaş
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="container mx-auto px-4 py-16 text-center">
          <span className="text-7xl mb-6 block">{post.emoji || '📝'}</span>
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="px-4 py-2 bg-white/20 text-white rounded-full text-sm font-bold">
              {post.category}
            </span>
            <span className="text-white/80 text-sm flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {post.created_at ? new Date(post.created_at).toLocaleDateString('tr-TR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              }) : ''}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white max-w-4xl mx-auto">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-xl text-white/80 mt-6 max-w-2xl mx-auto">
              {post.excerpt}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 -mt-8">
        <article className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-4xl mx-auto">
          {post.image_url && (
            <div 
              className="relative group cursor-pointer mb-8"
              onClick={() => setLightboxImage(post.image_url.startsWith('/') ? `${API_URL}${post.image_url}` : post.image_url)}
            >
              <img 
                src={post.image_url.startsWith('/') ? `${API_URL}${post.image_url}` : post.image_url}
                alt={post.title}
                className="w-full h-64 md:h-96 object-cover rounded-xl"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all rounded-xl flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-all bg-white/90 rounded-full p-3 shadow-lg">
                  <ZoomIn className="w-6 h-6 text-gray-800" />
                </div>
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">Büyütmek için tıklayın</p>
            </div>
          )}
          
          <div 
            className="blog-content prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={lightboxImage}
            alt={post?.title}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-black text-gray-900 text-center mb-8">
            Diğer Yazılar
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {relatedPosts.map((p) => (
              <div
                key={p.id}
                onClick={() => {
                  window.location.href = `/blog/${p.slug}`;
                }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all group cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <span className="text-4xl">{p.emoji || '📝'}</span>
                  <div>
                    <h3 className="font-bold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-2">
                      {p.title}
                    </h3>
                    <span className="text-sm text-gray-500">{p.category}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className={`bg-gradient-to-r ${settings?.cta_gradient || 'from-red-600 to-orange-500'} py-16`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-black text-white mb-4">
            {settings?.cta_title || 'İsveç Ehliyet Sınavına Hazır mısın?'}
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            {settings?.cta_subtitle || '2000+ güncel soru, Türkçe açıklamalar ve sınav simülasyonu ile başarıya ulaşın!'}
          </p>
          <a
            href="/?action=login"
            className="bg-white text-red-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center gap-2"
          >
            {settings?.cta_button_text || 'Ücretsiz Başla'}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white/70 py-8">
        <div className="container mx-auto px-4 text-center">
          <a href="/" className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-blue-600 rounded-lg flex items-center justify-center font-black text-white text-sm">
              E
            </div>
            <span className="text-xl font-bold text-white">Ehliyet.nu</span>
          </a>
          <p>© Ehliyet.nu - Tüm hakları saklıdır</p>
        </div>
      </footer>

      <style>{`
        .blog-content h2 {
          font-size: 1.75rem;
          font-weight: 800;
          color: #111827;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .blog-content h3 {
          font-size: 1.375rem;
          font-weight: 700;
          color: #1f2937;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .blog-content p {
          color: #4b5563;
          line-height: 1.8;
          margin-bottom: 1rem;
        }
        .blog-content ul, .blog-content ol {
          margin: 1rem 0;
          padding-left: 1.5rem;
        }
        .blog-content li {
          color: #4b5563;
          margin-bottom: 0.5rem;
          line-height: 1.7;
        }
        .blog-content strong {
          color: #111827;
          font-weight: 600;
        }
        .blog-content a {
          color: #dc2626;
          text-decoration: underline;
        }
        .blog-content a:hover {
          color: #b91c1c;
        }
      `}</style>
    </div>
  );
}
